export type OffsetPage<T> = {
  items: T[];
  total_count: number;
};

// Each page of an engine list is built from the whole matching set, so a burst
// of page reads multiplies the engine's database load. Cap how many are in flight.
export const PAGE_CONCURRENCY = 6;
const MAX_PAGES = 1000;

/**
 * Reads every page of an offset-paginated list. The first page's total sets the
 * remaining offsets, which are read concurrently; items come back in list order.
 * The first failed read fails the whole load.
 *
 * Only lists whose pages report `total_count` can be read this way: `readPage`
 * must resolve to an `OffsetPage`, and a page that arrives without a numeric
 * total (an untyped JSON body) fails the load rather than stopping at page one.
 */
export async function fetchAllPages<T>(
  readPage: (offset: number) => Promise<OffsetPage<T>>,
  label: string
): Promise<OffsetPage<T>> {
  const first = await readPage(0);
  const total: unknown = first.total_count;
  if (typeof total !== "number") {
    throw new Error(`${label} page has no total_count`);
  }
  // Step by the page size actually served, in case it is below the one requested.
  const pageSize = first.items.length;
  if (pageSize >= total) {
    return { items: first.items, total_count: total };
  }
  if (pageSize === 0) {
    throw new Error(`${label} pagination ended before total_count`);
  }
  const pageCount = Math.ceil(total / pageSize);
  if (pageCount > MAX_PAGES) {
    throw new Error(`${label} pagination exceeded ${MAX_PAGES} pages`);
  }
  const offsets = Array.from(
    { length: pageCount - 1 },
    (_, i) => (i + 1) * pageSize
  );

  const pages: T[][] = [first.items];
  let next = 0;
  let failed = false;
  const readRemaining = async () => {
    while (!failed && next < offsets.length) {
      const index = next;
      next += 1;
      try {
        const page = await readPage(offsets[index]);
        if (page.items.length === 0) {
          throw new Error(`${label} pagination ended before total_count`);
        }
        pages[index + 1] = page.items;
      } catch (error) {
        failed = true;
        throw error;
      }
    }
  };
  await Promise.all(
    Array.from(
      { length: Math.min(PAGE_CONCURRENCY, offsets.length) },
      readRemaining
    )
  );

  return { items: pages.flat(), total_count: total };
}
